<template>
  <div class="content mb-5">
    <b-row>
      <b-col class="mb-3">
        <h2>{{ this.$t("Admin Details") }}</h2>
      </b-col>
    </b-row>
    <b-form @submit="onSubmit" class="mb-5" v-if="!loading">
      <b-row>
        <b-col sm="6">
          <b-form-group label-for="fullname" :label="this.$t('Full Name')">
            <b-form-input
              id="fullname"
              data-lpignore="true"
              v-model="form.fullname"
              :state="$validateState($v.form.fullname)"
              @update="setValidationInput('fullname')"
            />
            <template #invalid-feedback>
              <p v-if="!$v.form.fullname.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group
            label-for="email"
            :label="this.$t('Email')"
            :state="$validateState($v.form.email)"
          >
            <b-form-input
              id="email"
              data-lpignore="true"
              v-model="form.email"
              :state="$validateState($v.form.email)"
              :placeholder="$t('e.g. name@company.com')"
              @update="setValidationInput('email')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.form.email.required">
                {{ $t("This is a required field.") }}
              </p>
              <p v-if="!$v.form.email.email">
                {{ $t("Please enter a valid email") }}
              </p>
            </template>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group
            label-for="description"
            :label="this.$t('Description (optional)')"
          >
            <b-form-input
              id="description"
              v-model="form.description"
              :placeholder="$t('Description')"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group label-for="password" :label="this.$t('Password')">
            <b-form-input
              id="password"
              type="password"
              data-lpignore="true"
              v-model="form.password"
              :state="$validateState($v.form.password)"
              :placeholder="$t('**********')"
              @update="setValidationInput('password')"
            />

            <template #invalid-feedback>
              <p v-if="$v.form.password.$dirty && !$v.form.password.minLength">
                {{
                  $t("X must be at least X characters long.", {
                    field: $t("Password"),
                    length: $v.form.password.$params.minLength.min,
                  })
                }}
              </p>
              <p
                v-else-if="
                  $v.form.password.$dirty && !$v.form.password.containsSpecial
                "
              >
                {{
                  $t("Password must contain a letter and a number or symbol")
                }}
              </p>
            </template>
          </b-form-group>
        </b-col>
      </b-row>
      <div class="d-flex justify-content-end">
        <b-button
          data-test-id="administrators-user-details-save"
          :disabled="fetching"
          @click="onSubmit"
        >
          {{ this.$t("Save Changes") }}
        </b-button>
      </div>
    </b-form>
    <hr class="mb-4 mt-4" />
    <TResetTwoFA
      v-if="selectedAdmin"
      :isTwoFAEnabled="selectedAdmin['2fa_enabled']"
      :id="selectedAdmin.id"
      @success="goToList"
    />

    <b-row v-if="loading" class="mb-5">
      <b-col cols="12" md="6">
        <b-skeleton animation="wave" width="85%"></b-skeleton>
        <b-skeleton animation="wave" width="55%"></b-skeleton>
        <b-skeleton animation="wave" width="70%"></b-skeleton>
      </b-col>
      <b-col cols="12" md="6">
        <b-skeleton animation="wave" width="85%"></b-skeleton>
        <b-skeleton animation="wave" width="55%"></b-skeleton>
        <b-skeleton animation="wave" width="70%"></b-skeleton>
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
  "name": "Customer Administrator",
  "meta": {
  "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";
import { required, minLength, email } from "vuelidate/lib/validators";
import TResetTwoFA from "@/components/TResetTwoFA";

export default {
  layout: "customer",
  components: { TResetTwoFA },
  methods: {
    ...mapActions("admins", ["fetchAdmin", "updateAdmin"]),
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    setValidationInput(object) {
      this.$v.form[`${object}`].$touch();
    },
    async onSubmit() {
      this.$v.$touch();
      this.fetching = true;
      if (!this.$v.$invalid) {
        try {
          if (this.form.password === "") delete this.form.password;
          this.form.msp_id = this.selectedMspUser
            ? this.selectedMspUser.id
            : "";
          this.form.user_id = this.selectedUser ? this.selectedUser.id : "";
          await this.updateAdmin({
            id: this.selectedAdmin.id,
            user: this.form,
          });
          this.displayToast({
            title: this.$t("Success"),
            message: "Updated",
            duration: 2000,
            variant: "success",
          });
        } finally {
          this.fetching = false;
          this.$v.form.$reset();
          this.goToList();
        }
      }
    },
    goToList() {
      this.$router.push("/customer/settings/administrators");
    },
  },
  computed: {
    ...mapGetters("admins", ["selectedAdmin"]),
    ...mapGetters("authentication", ["selectedUser", "selectedMspUser"]),
  },
  watch: {
    selectedAdmin() {
      this.form = {
        fullname: this.selectedAdmin.fullname || "",
        email: this.selectedAdmin.email || "",
        description: this.selectedAdmin.description || "",
        password: "",
        msp_id: "",
        user_id: "",
      };
    },
  },

  async created() {
    if (this.selectedUser.id) {
      await Promise.all([
        this.fetchAdmin({
          id: this.$route.params.id,
          msp_id: this.selectedMspUser ? this.selectedMspUser.id : null,
          user_id: this.selectedUser ? this.selectedUser.id : null,
        }),
      ]);

      this.form = {
        fullname: this.selectedAdmin.fullname || "",
        email: this.selectedAdmin.email || "",
        description: this.selectedAdmin.description || "",
        password: "",
        msp_id: "",
        user_id: "",
      };

      this.setPageTitle(this.selectedAdmin.fullname);
    }
    this.loading = false;
  },
  unmounted() {
    this.setPageTitle(null);
  },
  data() {
    return {
      loading: true,
      fetching: false,
      form: {
        fullname: null,
        email: null,
        description: null,
        password: null,
        msp_id: null,
        user_id: null,
      },
    };
  },
  validations: {
    form: {
      fullname: {
        required,
      },
      email: {
        required,
        email,
      },
      password: {
        minLength: minLength(8),
        containsSpecial(password) {
          return this.$validPassword(password);
        },
      },
      description: {},
    },
  },
};
</script>
